import React from 'react';
import { Textfit } from 'react-textfit';
import { Box, TextV2 } from '@withjoy/joykit';
import { pxToRem, useTheme } from '@withjoy/joykit/theme';
import { InlineEditor } from '@apps/guest/shared/components/InlineEditor';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { useScaleGuestSiteFontSize } from '@shared/core/website/theming/useScaleGuestSiteFontSize';
import { AuxTitleContainer } from './AuxFrameTitle.styles';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useTranslation } from '@shared/core';
import type { EventPageType } from '@graphql/generated-base';
import { Category } from '@graphql/generated-base';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { body4ToSubHeadingOverride, display6ToEventTitleOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { EVENT_DISPLAY_NAME_MAX_LENGTH } from '@shared/constants/textLimits';

interface Props
  extends Readonly<{
    x: 'left' | 'middle' | 'right';
    y: 'top' | 'middle' | 'bottom';
    textLight: boolean;
    title?: Maybe<string>;
    subtitle?: Maybe<string>;
    page: Maybe<EventPageType>;
    pageSlug: string;
    welcomePageId: string;
    isPageContainer?: boolean;
    zIndex?: number;
  }> {}

// Workaround to force the Textfit to recalculate its size on first load.
const noopOnReady = () => null;

export const AuxFrameTitle: React.FC<Props> = props => {
  const { x, y, textLight, title, subtitle, page, pageSlug, welcomePageId, isPageContainer, zIndex } = props;
  const textAlign = x === 'middle' ? 'center' : undefined;

  const { t } = useTranslation('joykit');
  const inlineEditorTransFn = t('inlineEditor');

  const isMobile = useIsMobileScreen();
  const { scaleFontSize } = useScaleGuestSiteFontSize();

  const handleEditTextClick = useEventCallback((action: 'editText' | 'editTextArea', textContent: string, label: string, pageId?: string) => {
    // send the message to the parent window to open the text editor
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value:
        action === 'editText'
          ? { action: 'editText', inlineEditData: { textContent, label, validationConstraints: { maxLength: EVENT_DISPLAY_NAME_MAX_LENGTH } } }
          : { action: 'editTextArea', inlineEditData: { textContent, label, pageId, isPageContainer: isPageContainer || false } },
    });
  });

  const handleEditFontClick = useEventCallback((category: Category) => {
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editFont', inlineEditData: { category } },
    });
  });

  const { typography } = useTheme();
  const eventTitleFontFamily = typography.variants.display6.fontFamily;

  return (
    <AuxTitleContainer
      y={y}
      x={x}
      color={textLight ? 'white' : '#333'}
      zIndex={zIndex}
    >
      {title ? (
        <InlineEditor
          elementLabel={inlineEditorTransFn.displayName()}
          actionData={{
            edit: () => handleEditTextClick('editText', title, inlineEditorTransFn.displayName()),
            font: () => handleEditFontClick(Category.EVENT_TITLE),
          }}
          wrapperCSS={{
            zIndex: 'banner',
            ...(isMobile ? { width: '100%', display: 'flex', height: '100%', flexDirection: 'column', justifyContent: 'flex-end' } : {}),
          }}
          componentName="eventDisplayName"
          pageName={page}
          pageSlug={pageSlug}
        >
          <GuestSiteTypographyOverride override={display6ToEventTitleOverride}>
            {isMobile ? (
              <TextV2
                typographyVariant="display6"
                textAlign={textAlign}
                tagName="div"
                fontSize="auto"
                display="flex"
                width="100%"
                height="100%"
                justifyContent="flex-end"
                flexDirection="column"
                __css={{
                  ['& > div']: {
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    flexDirection: 'column',
                  },
                }}
              >
                <Textfit
                  // Need to use fontFamily as part of the key to force the Textfit to recalculate its size on change
                  key={`title-text-fit-${scaleFontSize(pxToRem(56), Category.EVENT_TITLE)}-${eventTitleFontFamily}`}
                  // Stable reference: Textfit re-fits whenever any prop changes identity, so an
                  // inline arrow made EVERY parent re-render (e.g. the slick chunk landing) re-fit
                  // and jump the bottom-anchored title (measured 118px, part of aloha CLS 0.42).
                  onReady={noopOnReady}
                  // convert the font size to pixels, max take font size in pixels
                  max={Number.parseFloat(scaleFontSize(pxToRem(56), Category.EVENT_TITLE)) * 16}
                >
                  {title}
                </Textfit>
              </TextV2>
            ) : (
              <TextV2
                // The LCP element, large block-level text
                as="h1"
                typographyVariant="display6"
                textAlign={textAlign}
              >
                {title}
              </TextV2>
            )}
          </GuestSiteTypographyOverride>
        </InlineEditor>
      ) : null}
      {subtitle ? (
        <Box
          marginTop={[5]}
          __css={{ flexShrink: 0 }}
        >
          <InlineEditor
            elementLabel={inlineEditorTransFn.greetings()}
            actionData={{
              edit: () => handleEditTextClick('editTextArea', subtitle, inlineEditorTransFn.greetings(), welcomePageId),
              font: () => handleEditFontClick(Category.SUB_HEADING),
            }}
            wrapperCSS={{
              zIndex: 'banner',
            }}
            componentName="greeting"
            pageName={page}
            pageSlug={pageSlug}
          >
            {/* Scroll only the text, so a long greeting can't push the title out of the frame and the editor chrome isn't clipped */}
            {/* Padding + negative margin hold script-font descenders that overflow the line box, so a short greeting gets no scrollbar */}
            <Box
              paddingBottom={4}
              marginBottom={-4}
              __css={{ maxHeight: '40vh', overflowY: 'auto', pointerEvents: 'auto' }}
            >
              <GuestSiteTypographyOverride override={body4ToSubHeadingOverride}>
                <TextV2
                  typographyVariant="body4"
                  textAlign={textAlign}
                  whiteSpace="pre-wrap"
                >
                  {subtitle}
                </TextV2>
              </GuestSiteTypographyOverride>
            </Box>
          </InlineEditor>
        </Box>
      ) : null}
    </AuxTitleContainer>
  );
};
